import localforage from 'localforage';

const CACHE_KEY = 'github_contributions_hadleyswello';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const GITHUB_GRAPHQL_API = 'https://api.github.com/graphql';
const USERNAME = 'HadleySwello';
const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN;
const IS_DEV = process.env.NODE_ENV === 'development';

// Calculate date range for last 1 year (GitHub's maximum)
function getDateRange() {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 365); // 1 year = 365 days (GitHub max)

  return {
    from: startDate.toISOString(),
    to: endDate.toISOString(),
  };
}

// Check if cached data is still valid
async function getCachedData() {
  try {
    const cached = await localforage.getItem(CACHE_KEY);
    if (!cached) {
      return null;
    }

    const { data, timestamp } = cached;
    const now = Date.now();

    // Check if cache is expired
    // Don't delete expired cache here - preserve it for fallback scenarios
    if (now - timestamp > CACHE_DURATION) {
      return null;
    }

    return data;
  } catch (error) {
    return null;
  }
}

// Store data in cache
async function setCachedData(data) {
  try {
    await localforage.setItem(CACHE_KEY, {
      data,
      timestamp: Date.now(),
    });
  } catch (error) {
    // Silently fail cache writes
  }
}

// Fetch contributions from GitHub GraphQL API
async function fetchContributionsFromAPI() {
  const { from, to } = getDateRange();

  const query = `
    query {
      user(login: "${USERNAME}") {
        contributionsCollection(from: "${from}", to: "${to}") {
          contributionCalendar {
            totalContributions
            weeks {
              firstDay
              contributionDays {
                date
                contributionCount
                weekday
                color
              }
            }
            months {
              name
              firstDay
              totalWeeks
              year
            }
          }
        }
      }
    }
  `;

  // Build headers with optional authentication
  const headers = {
    'Content-Type': 'application/json',
  };

  if (GITHUB_TOKEN) {
    headers.Authorization = `Bearer ${GITHUB_TOKEN}`;
  }

  const response = await fetch(GITHUB_GRAPHQL_API, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query }),
  });

  // Check HTTP status before parsing JSON
  if (!response.ok) {
    if (response.status === 403) {
      throw new Error(
        'GitHub API requires authentication. Please set REACT_APP_GITHUB_TOKEN in your environment variables. See README for instructions.'
      );
    }
    throw new Error(
      `GitHub API error: ${response.status} ${response.statusText}`
    );
  }

  const result = await response.json();

  if (result.errors) {
    // Handle GraphQL errors (like rate limiting or auth issues)
    const errorMessage =
      result.errors[0]?.message || 'Failed to fetch contributions';

    // Provide more specific error messages
    if (
      errorMessage.includes('rate limit') ||
      errorMessage.includes('API rate limit')
    ) {
      throw new Error(
        'GitHub API rate limit exceeded. Please try again later.'
      );
    }

    if (
      errorMessage.includes('authenticate') ||
      errorMessage.includes('login')
    ) {
      throw new Error(
        'GitHub API requires authentication. Please set REACT_APP_GITHUB_TOKEN in your environment variables. See README for instructions.'
      );
    }

    throw new Error(errorMessage);
  }

  if (!result.data || !result.data.user) {
    throw new Error('User not found');
  }

  const contributionsCollection = result.data.user.contributionsCollection;

  if (!contributionsCollection) {
    throw new Error('No contributions data available');
  }

  // Flatten the weeks array into daily contributions
  const contributions = [];
  contributionsCollection.contributionCalendar.weeks.forEach(week => {
    week.contributionDays.forEach(day => {
      contributions.push({
        date: day.date,
        count: day.contributionCount,
        weekday: day.weekday,
      });
    });
  });

  return {
    contributions,
    totalContributions:
      contributionsCollection.contributionCalendar.totalContributions,
  };
}

// Main function to get contributions (with caching)
export async function getGitHubContributions() {
  // Skip cache in development
  if (!IS_DEV) {
    // Try cache first
    const cached = await getCachedData();
    if (cached) {
      return cached;
    }
  }

  // Fetch from API
  try {
    const data = await fetchContributionsFromAPI();
    // Only cache in production
    if (!IS_DEV) {
      // Delete any expired cache before storing new data
      const existingCache = await localforage.getItem(CACHE_KEY);
      if (existingCache) {
        const now = Date.now();
        if (now - existingCache.timestamp > CACHE_DURATION) {
          await localforage.removeItem(CACHE_KEY);
        }
      }
      await setCachedData(data);
    }
    return data;
  } catch (error) {
    // If fetch fails, try to return stale cache if available (only in production)
    if (!IS_DEV) {
      const staleCache = await localforage.getItem(CACHE_KEY);
      if (staleCache && staleCache.data) {
        return staleCache.data;
      }
    }
    throw error;
  }
}
